<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useEventListener } from "@vueuse/core";
import CoreButton from "@/components/common/CoreButton/CoreButton.vue";
import { useRootStore } from "@/stores";
import { SAVELOADING } from "@/stores/constants";

export interface Props {
  visible: boolean;
  showCancel?: boolean;
  saveLoading?: boolean;
  saveEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showCancel: true,
  saveEnabled: true,
});

const { t } = useI18n();

const rootStore = useRootStore();
const { hasUnsavedFormChanges } = storeToRefs(rootStore);

const emit = defineEmits<{
  (e: "cancel"): void;
  (e: "save"): void;
  (e: "close"): void;
}>();

const buttonTabindex = computed(() => (props.visible ? 0 : -1));

const showSaveIcon = ref(false);

watch(
  () => props.saveLoading,
  (newValue) => {
    if (newValue === SAVELOADING.PENDING || newValue === SAVELOADING.CLEAR) {
      return;
    }

    showSaveIcon.value = true;

    setTimeout(() => {
      emit("close");

      setTimeout(() => {
        showSaveIcon.value = false;
      }, 500);
    }, 500);
  }
);

const flash = ref(false);

const doFlash = () => {
  flash.value = true;
  setTimeout(() => {
    flash.value = false;
  }, 200);
};

const clickEventHandler = () => {
  if (hasUnsavedFormChanges.value) {
    doFlash();
  }
};

const keyUpEventHandler = (event: KeyboardEvent) => {
  if (event.code === "Escape" && hasUnsavedFormChanges.value) {
    doFlash();
  }
};

useEventListener("click", clickEventHandler);
useEventListener("keyup", keyUpEventHandler);
</script>

<template>
  <div
    v-if="props.visible"
    class="container"
    :class="{ 'visible actions': props.visible, flash }"
  >
    <div>
      <slot />
    </div>
    <div class="buttons">
      <CoreButton
        v-if="props.showCancel"
        class="save-button"
        :tabindex="buttonTabindex"
        @click="emit('cancel')"
      >
        {{ t("saveDialog.cancel") }}
      </CoreButton>

      <div
        v-if="showSaveIcon"
        class="saved"
      >
        <font-awesome-icon :icon="['fal', 'circle-check']" />
        <span>{{ t("saveDialog.saved") }}</span>
      </div>
      <CoreButton
        v-else
        type="primary"
        class="save-button"
        :loading="saveLoading"
        :tabindex="buttonTabindex"
        :disabled="!saveEnabled"
        @click="emit('save')"
      >
        {{ t("saveDialog.save") }}
      </CoreButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-direction: column;
}

.actions {
  border-radius: 5px;
  position: absolute;
  left: 0;
  bottom: -90px;
  padding: 15px;
  transition-property: transform, bottom, background;
  transition-duration: 0.2s, 0.2s, 0.3s;
  transition-timing-function: ease-in-out;
  transition-delay: 0s;
  width: 100%;
  box-shadow: var(--so-box-shadow);
  z-index: 9999;
  background: var(--so-bg-color);
}

.actions.visible {
  bottom: 0;
}

.actions.flash {
  transform: scale(1.05);
  background: #9edded;
}

.actions span {
  font-size: 0.875rem;
  color: var(--so-text-color-primary);
}

.actions div {
  display: flex;
  align-items: center;
}

.buttons {
  justify-content: end;
}

.saved {
  margin-left: 12px;
  width: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--so-color-success);

  span {
    margin-left: 5px;
    color: var(--so-color-success);
  }
}
</style>
